import { useEffect, useState } from 'react'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { Popover } from './Popover'
import type { SavedViewsConfig } from './types'

export interface SavedViewsMenuProps {
  config: SavedViewsConfig
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Saved Views / Filters — personal (`user`) scope is server-persisted per-user (see
 * `docs/design-system.md` §3.2 and `lib/savedViews.ts`, ADR-0058). IB-27 S4a (ADR-0059) adds a
 * shared `tenant`-default scope: capability-gated (`saved_view:A`) to author/manage, but visible to
 * every user in the tenant with no capability needed to read it. Name and save the list's current
 * filter/sort/column configuration, list every saved view, apply one in a click, rename, delete,
 * and mark a PERSONAL one as the default (auto-applied next time this list loads if no tenant-
 * default outranks it — see `resolveDefaultView`). `team-shared` stays disclosed as "coming soon"
 * (S4b — needs a team-grouping primitive that does not exist yet, deliberately not invented here).
 */
export function SavedViewsMenu({ config, open, onOpenChange }: SavedViewsMenuProps) {
  const [mode, setMode] = useState<'list' | 'save' | { renaming: string }>('list')
  const [nameInput, setNameInput] = useState('')
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null)
  const [tenantMode, setTenantMode] = useState<'idle' | 'save' | 'renaming'>('idle')
  const [tenantNameInput, setTenantNameInput] = useState('')

  const personalViews = config.views.filter((v) => (v.scope ?? 'user') === 'user')
  const tenantDefaultView = config.views.find((v) => v.scope === 'tenant') ?? null
  const tenantDefaultOffered = typeof config.onSaveNewTenantDefault === 'function'
  const tenantDefaultDenied = Boolean(config.tenantDefaultCapabilityDenied)

  // A REAL 403 firing (design §6.4) collapses any open tenant-save/-rename input back to the
  // (now-disabled, tooltip-annotated) idle affordance — the server denial is the one true signal,
  // never a client-side guess; there is nothing useful left to retype once it has actually fired.
  useEffect(() => {
    if (tenantDefaultDenied) setTenantMode('idle')
  }, [tenantDefaultDenied])

  function reset() {
    setMode('list')
    setNameInput('')
    setConfirmingDeleteId(null)
  }

  function resetTenant() {
    setTenantMode('idle')
    setTenantNameInput('')
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      reset()
      resetTenant()
    }
    onOpenChange(next)
  }

  function startSave() {
    setNameInput('')
    setMode('save')
  }

  function commitSave() {
    const trimmed = nameInput.trim()
    if (!trimmed) return
    config.onSaveNew(trimmed)
    reset()
  }

  function startRename(id: string, currentName: string) {
    setNameInput(currentName)
    setMode({ renaming: id })
  }

  function commitRename(id: string) {
    const trimmed = nameInput.trim()
    if (!trimmed) return
    config.onRename(id, trimmed)
    reset()
  }

  function startSaveTenantDefault() {
    setTenantNameInput('')
    setTenantMode('save')
  }

  function startRenameTenantDefault() {
    if (!tenantDefaultView) return
    setTenantNameInput(tenantDefaultView.name)
    setTenantMode('renaming')
  }

  function commitTenantName() {
    const trimmed = tenantNameInput.trim()
    if (!trimmed || !config.onSaveNewTenantDefault) return
    if (tenantMode === 'renaming' && tenantDefaultView) {
      config.onRename(tenantDefaultView.id, trimmed)
    } else {
      config.onSaveNewTenantDefault(trimmed)
    }
    resetTenant()
  }

  return (
    <Popover
      label="Saved views"
      open={open}
      onOpenChange={handleOpenChange}
      width={300}
      triggerContent={
        <>
          <Icon name="bookmark" size={14} /> Views
          {config.isDirty ? <span aria-hidden="true" style={{ color: 'var(--iw-accent)' }}> •</span> : null}
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
        {config.error ? <InlineAlert severity="error">{config.error}</InlineAlert> : null}
        <div>
          <div
            style={{
              fontSize: 'var(--iw-size-xs)',
              fontWeight: 'var(--iw-weight-medium)' as never,
              color: 'var(--iw-text-muted)',
              marginBottom: 4,
            }}
          >
            Personal
          </div>

          {personalViews.length === 0 ? (
            <p style={{ margin: 0, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-subtle)' }}>
              No saved views yet. Save your current filters, sort, and columns to reuse them next time.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {personalViews.map((view) => {
                const isRenaming = typeof mode === 'object' && mode.renaming === view.id
                const isConfirmingDelete = confirmingDeleteId === view.id
                return (
                  <li key={view.id} style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 2px' }}>
                    {isRenaming ? (
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <input
                          className="iw-input"
                          aria-label={`Rename saved view "${view.name}"`}
                          value={nameInput}
                          autoFocus
                          onChange={(e) => setNameInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRename(view.id)
                            if (e.key === 'Escape') reset()
                          }}
                        />
                        <button
                          type="button"
                          className="iw-icon-btn-sm"
                          aria-label="Confirm rename"
                          disabled={!nameInput.trim()}
                          onClick={() => commitRename(view.id)}
                        >
                          <Icon name="check" size={13} />
                        </button>
                        <button type="button" className="iw-icon-btn-sm" aria-label="Cancel rename" onClick={reset}>
                          <Icon name="x" size={13} />
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <button
                          type="button"
                          className="iw-btn iw-btn--sm"
                          style={{ flex: 1, justifyContent: 'flex-start' }}
                          onClick={() => {
                            config.onApply(view.id)
                            handleOpenChange(false)
                          }}
                        >
                          {view.name}
                        </button>
                        <button
                          type="button"
                          className="iw-icon-btn-sm"
                          aria-pressed={view.isDefault}
                          aria-label={view.isDefault ? `Unset "${view.name}" as default` : `Set "${view.name}" as default`}
                          title={view.isDefault ? 'Default — auto-applied next visit' : 'Set as default'}
                          onClick={() => (view.isDefault ? config.onUnsetDefault() : config.onSetDefault(view.id))}
                          style={view.isDefault ? { color: 'var(--iw-accent)' } : undefined}
                        >
                          <Icon name="star" size={13} />
                        </button>
                        <button
                          type="button"
                          className="iw-icon-btn-sm"
                          aria-label={`Rename "${view.name}"`}
                          onClick={() => startRename(view.id, view.name)}
                        >
                          <Icon name="edit-3" size={13} />
                        </button>
                        <button
                          type="button"
                          className="iw-icon-btn-sm"
                          aria-label={`Delete "${view.name}"`}
                          onClick={() => setConfirmingDeleteId(view.id)}
                        >
                          <Icon name="trash-2" size={13} />
                        </button>
                      </div>
                    )}
                    {isConfirmingDelete ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 'var(--iw-size-xs)',
                          color: 'var(--iw-text-muted)',
                        }}
                      >
                        Delete this view?
                        <button
                          type="button"
                          className="iw-btn iw-btn--sm"
                          onClick={() => {
                            config.onDelete(view.id)
                            setConfirmingDeleteId(null)
                          }}
                        >
                          Delete
                        </button>
                        <button type="button" className="iw-btn iw-btn--sm" onClick={() => setConfirmingDeleteId(null)}>
                          Cancel
                        </button>
                      </div>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          )}

          {config.isDirty ? (
            <p style={{ margin: '4px 0 0', fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-subtle)' }}>
              Current filters/sort/columns differ from the applied view.
            </p>
          ) : null}

          {mode === 'save' ? (
            <div style={{ display: 'flex', gap: 4, marginTop: 6, alignItems: 'center' }}>
              <input
                className="iw-input"
                aria-label="New saved view name"
                placeholder="View name…"
                value={nameInput}
                autoFocus
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitSave()
                  if (e.key === 'Escape') reset()
                }}
              />
              <button
                type="button"
                className="iw-btn iw-btn--primary iw-btn--sm"
                disabled={!nameInput.trim()}
                onClick={commitSave}
              >
                Save
              </button>
              <button type="button" className="iw-btn iw-btn--sm" onClick={reset}>
                Cancel
              </button>
            </div>
          ) : (
            <button type="button" className="iw-btn iw-btn--sm" style={{ marginTop: 6 }} onClick={startSave}>
              <Icon name="plus" size={12} /> Save current view…
            </button>
          )}
        </div>

        <div title="Team-shared saved views ship in a later story (need a team-grouping primitive that does not exist yet)" style={{ opacity: 0.55 }}>
          <div
            style={{
              fontSize: 'var(--iw-size-xs)',
              fontWeight: 'var(--iw-weight-medium)' as never,
              color: 'var(--iw-text-muted)',
              marginBottom: 4,
            }}
          >
            Team-shared <span className="iw-pill iw-pill--muted">Coming soon</span>
          </div>
          <button type="button" className="iw-btn iw-btn--sm" disabled>
            Save as team view
          </button>
        </div>

        {tenantDefaultOffered ? (
          <div>
            <div
              style={{
                fontSize: 'var(--iw-size-xs)',
                fontWeight: 'var(--iw-weight-medium)' as never,
                color: 'var(--iw-text-muted)',
                marginBottom: 4,
              }}
            >
              Tenant default
            </div>

            {tenantDefaultView ? (
              tenantMode === 'renaming' ? (
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    className="iw-input"
                    aria-label={`Rename tenant-default view "${tenantDefaultView.name}"`}
                    value={tenantNameInput}
                    autoFocus
                    onChange={(e) => setTenantNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitTenantName()
                      if (e.key === 'Escape') resetTenant()
                    }}
                  />
                  <button
                    type="button"
                    className="iw-icon-btn-sm"
                    aria-label="Confirm rename"
                    disabled={!tenantNameInput.trim()}
                    onClick={commitTenantName}
                  >
                    <Icon name="check" size={13} />
                  </button>
                  <button type="button" className="iw-icon-btn-sm" aria-label="Cancel rename" onClick={resetTenant}>
                    <Icon name="x" size={13} />
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button
                    type="button"
                    className="iw-btn iw-btn--sm"
                    style={{ flex: 1, justifyContent: 'flex-start' }}
                    onClick={() => {
                      config.onApply(tenantDefaultView.id)
                      handleOpenChange(false)
                    }}
                  >
                    <Icon name="star" size={13} /> {tenantDefaultView.name}
                  </button>
                  <button
                    type="button"
                    className="iw-icon-btn-sm"
                    aria-label={`Rename "${tenantDefaultView.name}"`}
                    disabled={tenantDefaultDenied}
                    title={tenantDefaultDenied ? "You don't have permission to manage the firm's default view." : undefined}
                    onClick={startRenameTenantDefault}
                  >
                    <Icon name="edit-3" size={13} />
                  </button>
                  <button
                    type="button"
                    className="iw-icon-btn-sm"
                    aria-label={`Delete "${tenantDefaultView.name}"`}
                    disabled={tenantDefaultDenied}
                    title={tenantDefaultDenied ? "You don't have permission to manage the firm's default view." : undefined}
                    onClick={() => setConfirmingDeleteId(tenantDefaultView.id)}
                  >
                    <Icon name="trash-2" size={13} />
                  </button>
                </div>
              )
            ) : (
              <p style={{ margin: '0 0 4px', fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-subtle)' }}>
                No tenant-default view yet. Publishing one auto-applies it for anyone in your firm who
                hasn't set a personal default.
              </p>
            )}

            {tenantDefaultView && confirmingDeleteId === tenantDefaultView.id ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 'var(--iw-size-xs)',
                  color: 'var(--iw-text-muted)',
                  marginTop: 4,
                }}
              >
                Delete this tenant-default view?
                <button
                  type="button"
                  className="iw-btn iw-btn--sm"
                  onClick={() => {
                    config.onDelete(tenantDefaultView.id)
                    setConfirmingDeleteId(null)
                  }}
                >
                  Delete
                </button>
                <button type="button" className="iw-btn iw-btn--sm" onClick={() => setConfirmingDeleteId(null)}>
                  Cancel
                </button>
              </div>
            ) : null}

            {tenantMode === 'save' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    className="iw-input"
                    aria-label="Tenant-default view name"
                    placeholder="View name…"
                    value={tenantNameInput}
                    autoFocus
                    onChange={(e) => setTenantNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitTenantName()
                      if (e.key === 'Escape') resetTenant()
                    }}
                  />
                  <button
                    type="button"
                    className="iw-btn iw-btn--primary iw-btn--sm"
                    disabled={!tenantNameInput.trim()}
                    onClick={commitTenantName}
                  >
                    Publish
                  </button>
                  <button type="button" className="iw-btn iw-btn--sm" onClick={resetTenant}>
                    Cancel
                  </button>
                </div>
                <p style={{ margin: 0, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-subtle)' }}>
                  Shared with: everyone in your firm.
                </p>
              </div>
            ) : !tenantDefaultView ? (
              <button
                type="button"
                className="iw-btn iw-btn--sm"
                disabled={tenantDefaultDenied}
                title={tenantDefaultDenied ? "You don't have permission to publish a tenant-default view for this firm." : undefined}
                onClick={startSaveTenantDefault}
              >
                <Icon name="plus" size={12} /> Save as tenant default
              </button>
            ) : null}
          </div>
        ) : (
          <div title="Tenant-default saved views are not offered for this list" style={{ opacity: 0.55 }}>
            <div
              style={{
                fontSize: 'var(--iw-size-xs)',
                fontWeight: 'var(--iw-weight-medium)' as never,
                color: 'var(--iw-text-muted)',
                marginBottom: 4,
              }}
            >
              Tenant-default <span className="iw-pill iw-pill--muted">Coming soon</span>
            </div>
            <button type="button" className="iw-btn iw-btn--sm" disabled>
              Save as tenant default
            </button>
          </div>
        )}

        <p style={{ margin: 0, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-subtle)', borderTop: '1px solid var(--iw-border)', paddingTop: 6 }}>
          Saved views are personal to you and sync across your devices. A tenant-default (if
          published) auto-applies for anyone in your firm without a personal default. Team-shared
          views are coming soon.
        </p>
      </div>
    </Popover>
  )
}
